import { api } from './api';
import { rateLimitFromError } from './rateLimit';
import type {
  Couple,
  CoupleError,
  CoupleErrorCode,
  CoupleOverview,
  CouplePartner,
  NudgeType,
  PendingInvite,
} from '../types/couple';

/**
 * Everything the app knows about the couple endpoints lives here, so a change
 * to the contract is a change to this file (and `coupleSlice`) only.
 */

export const INVITE_CODE_LENGTH = 6;

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set<CoupleErrorCode>([
  'already_coupled',
  'own_invite',
  'invite_already_used',
  'invite_not_found',
  'no_active_couple',
  'email_unverified',
  'too_many_attempts',
]);

/** Uppercases and drops spaces, dashes and anything that is not a letter or digit. */
export function normaliseInviteCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, INVITE_CODE_LENGTH);
}

/**
 * Maps an Axios failure to a stable code the UI can translate. `context`
 * disambiguates a plain 422: a missing code on join, a wrong password on unlink.
 */
export function toCoupleError(
  error: unknown,
  context: 'join' | 'unlink' | 'other' = 'other'
): CoupleError {
  const response = (
    error as {
      response?: { status?: number; data?: { error?: unknown } };
    } | null
  )?.response;
  if (!response) return { code: 'network', status: null };

  const status = typeof response.status === 'number' ? response.status : null;
  const apiCode = response.data?.error;
  if (typeof apiCode === 'string' && KNOWN_ERROR_CODES.has(apiCode)) {
    return { code: apiCode as CoupleErrorCode, status };
  }
  if (status === 429) {
    return {
      code: 'rate_limited',
      status,
      retryAfterMs: rateLimitFromError(error)?.retryAfterMs ?? null,
    };
  }
  if (status === 422 && context === 'join') {
    return { code: 'code_required', status };
  }
  if (status === 422 && context === 'unlink') {
    return { code: 'wrong_password', status };
  }
  return { code: 'unknown', status };
}

function toPartner(raw: unknown): CouplePartner | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.id !== 'number') return null;
  return {
    id: item.id,
    name: typeof item.name === 'string' ? item.name : '',
    last_seen_at:
      typeof item.last_seen_at === 'string' ? item.last_seen_at : null,
  };
}

function toCouple(raw: unknown): Couple | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.id !== 'number') return null;
  return {
    id: item.id,
    status: 'active',
    started_at: typeof item.started_at === 'string' ? item.started_at : null,
    ...(typeof item.host_user_id === 'number'
      ? { host_user_id: item.host_user_id }
      : {}),
    ...(typeof item.is_host === 'boolean' ? { is_host: item.is_host } : {}),
  };
}

function toInvite(raw: unknown): PendingInvite | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const item = raw as Record<string, unknown>;
  if (typeof item.code !== 'string' || item.code === '') return null;
  return {
    code: item.code,
    expires_at: typeof item.expires_at === 'string' ? item.expires_at : '',
    link: typeof item.link === 'string' ? item.link : '',
  };
}

/** Tolerant parse of `GET /couple` (and the `{ couple, partner }` join reply). */
export function normaliseOverview(payload: unknown): CoupleOverview {
  const data =
    typeof payload === 'object' && payload !== null
      ? (payload as Record<string, unknown>)
      : {};
  const couple = toCouple(data.couple);
  return {
    couple,
    partner: couple ? toPartner(data.partner) : null,
    pending_invite: couple ? null : toInvite(data.pending_invite),
  };
}

/** GET /couple */
export async function requestCouple(): Promise<CoupleOverview> {
  const response = await api.get('/couple');
  return normaliseOverview(response.data);
}

/** POST /couple/invites (201; idempotent within 24 h) */
export async function requestCreateInvite(): Promise<PendingInvite> {
  const response = await api.post('/couple/invites');
  const invite = toInvite(response.data);
  if (!invite) throw new Error('Malformed invite response');
  return invite;
}

/** POST /couple/join */
export async function requestJoinCouple(code: string): Promise<CoupleOverview> {
  const response = await api.post('/couple/join', {
    code: normaliseInviteCode(code),
  });
  return normaliseOverview(response.data);
}

/** DELETE /couple (204). A guest has no password and sends none. */
export async function requestUnlinkCouple(password?: string): Promise<void> {
  if (password === undefined) {
    await api.delete('/couple');
    return;
  }
  await api.delete('/couple', { data: { password } });
}

/** POST /couple/nudge (202; 10 per hour) */
export async function requestNudge(type: NudgeType): Promise<void> {
  await api.post('/couple/nudge', { type });
}

/** "Seen within the last 90 s" counts as online when no socket says otherwise. */
export const ONLINE_FRESHNESS_MS = 90_000;

export function isFreshlySeen(
  lastSeenAt: string | null | undefined,
  now: number = Date.now()
): boolean {
  if (!lastSeenAt) return false;
  const seen = Date.parse(lastSeenAt);
  return Number.isFinite(seen) && now - seen <= ONLINE_FRESHNESS_MS;
}
